import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import store from 'store'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'

import Progress from '../../../components/pages/Register/Progress'

import {
  selectAccessToken,
  selectFullScreenMode,
  selectRegisterStep,
  selectUserData,
  selectUserLoading,
} from '../../../routing/fetchingfunction/user/user.reducer'
import { setStep } from '../../../routing/fetchingfunction/user/user.action'

import {
  studentRegistrationSteps,
  tutorRegistrationSteps,
  BACKEND_REGISTRATION_CODES,
} from 'constants'

import MobileComplianceMobile from '../../../components/mobileComplianceModal/MobileComplianceModal'
import { useAvailableCategories } from 'hooks'

// Map URL path segments to step codes
const pathToStepMap = {
  'create-account': 'create_account',
  'confirm-email': 'email_verification',
  'email-verification': 'email_verification',
  'profile-update': 'profile_update',
  goals: 'student_goals',
  'pass-test': 'tutor_test',
  kyc: 'tutor_kyc',
  'create-banner': 'tutor_create_banner',
  complete: 'profile_completed',
}

// Map step codes to URL path segments
const stepToPathMap = {
  create_account: 'create-account',
  email_verification: 'confirm-email',
  profile_update: 'profile-update',
  student_goals: 'goals',
  tutor_test: 'pass-test',
  tutor_kyc: 'kyc',
  tutor_create_banner: 'create-banner',
  profile_completed: 'complete',
}

// Helper to get the current step code from the URL path
const getStepCodeFromPath = (pathSuffix, role) => {
  const pathParts = pathSuffix.split('/').filter(Boolean)
  const pathSegment = pathParts[0] || 'create-account'
  const stepCode = pathToStepMap[pathSegment] || 'create_account'

  console.log(`[getStepCodeFromPath] URL path "${pathSegment}" maps to step code "${stepCode}"`)

  return stepCode
}

// Helper to get the URL path for a step code
const getPathForStep = (stepCode) => {
  return stepToPathMap[stepCode] || 'create-account'
}

export const Register = ({ role = 'student' }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const { '*': pathSuffix = '' } = useParams()
  const fullscreen = useSelector(selectFullScreenMode)

  const currentStepCode = useSelector(selectRegisterStep)
  const accessToken = useSelector(selectAccessToken) //|| localStorage.getItem('access_token')
  const userData = useSelector(selectUserData)
  const isLoading = useSelector(selectUserLoading)
  const [isMobile, setIsMobile] = useState(false)

  const { takenTests, loading: testLoading } = useAvailableCategories('passed')

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600)
    }

    handleResize()

    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Get the current step from URL or Redux
  const currentStepFromUrl = useMemo(() => {
    return getStepCodeFromPath(pathSuffix, role)
  }, [pathSuffix, role])

  // Force reset to create_account if user is not logged in and tries to access a later step
  useEffect(() => {
    // Print the current URL path for debugging
    if (isLoading || accessToken) {
      return
    }
    console.log(
      `[Register Security Check] Current path: ${pathname}, currentStepFromUrl: ${currentStepFromUrl} ${pathSuffix}`
    )

    // Special case for email verification - always allow this step without login
    if (pathname.includes('/email-verification') || pathname.includes('/confirm-email')) {
      console.log('[Register Security Check] Email verification page detected, allowing access')
      return
    }

    // Check if we're trying to access a step other than create_account without being logged in
    const isAccessingProtectedStep = currentStepFromUrl !== 'create_account'
    const hasNoSession = !accessToken || !userData

    if (isAccessingProtectedStep && hasNoSession) {
      console.log(
        '[Register Security Check] Attempting to access protected step without login session'
      )
      console.log('[Register Security Check] Redirecting to first step (create-account)')

      // Clear any stored step first to prevent automatic redirects
      localStorage.removeItem('register_step')
      store.remove('register_step')

      // Redirect to the first step
      navigate(`/${role}/register/create-account`, { replace: true })

      // Set the step in Redux
      dispatch(
        setStep({
          step: 'create_account',
          skipBackendUpdate: true,
        })
      )
    }
  }, [currentStepFromUrl, accessToken, userData, navigate, role, dispatch, pathname])

  // Get the effective step code to use
  const effectiveStepCode = currentStepFromUrl || currentStepCode || 'create_account'
  console.log(effectiveStepCode, currentStepCode, isLoading)

  // We need a ref to track if we've already handled a step update
  const [isProcessingStepUpdate, setIsProcessingStepUpdate] = React.useState(false)

  // Track URL-based state updates to prevent loops
  const lastUrlUpdate = useRef({ time: 0, step: null })

  // Use a single useEffect to handle step synchronization between URL and Redux
  useEffect(() => {
    // Skip if we're already processing an update
    if (isProcessingStepUpdate || isLoading || testLoading) {
      return
    }

    try {
      setIsProcessingStepUpdate(true)

      console.log('[Register Sync] Starting step synchronization between URL and Redux', {
        currentStepFromUrl,
        currentStepCode,
        path: pathname,
      })

      // Handle email verification page specially
      if (pathname.includes('/email-verification') || pathname.includes('/confirm-email')) {
        // Make sure Redux has the correct step
        if (currentStepCode !== 'email_verification') {
          console.log('[Register Sync] Forcing email_verification step in Redux')
          dispatch(
            setStep({
              step: 'email_verification',
              skipBackendUpdate: true,
            })
          )
          store.set('register_step', 'email_verification')
          localStorage.setItem('register_step', 'email_verification')

          // Also check if email is already confirmed in localStorage
          const isConfirmed = localStorage.getItem('email_confirmed') === 'true'
          console.log(`[Register Sync] Email confirmed status from localStorage: ${isConfirmed}`)
          return
        }
      }

      // If user is logged in and on the login form page, skip directly to profile-update
      if (accessToken && window.location.pathname.includes('/login')) {
        console.log('User already logged in on login page. Redirecting to profile-update.')
        navigate(`/${role}/register/profile-update`, { replace: true })
        return
      }

      // Get the current step from URL and Redux
      const tabs = role === 'student' ? studentRegistrationSteps : tutorRegistrationSteps
      const currentStepIndex = tabs.findIndex((t) => t.code === currentStepCode)
      const urlStepIndex = tabs.findIndex((t) => t.code === currentStepFromUrl)

      // If we don't have a valid step from URL or Redux, default to create_account
      if (currentStepFromUrl === 'create_account' && !accessToken) {
        console.log('User is on create_account and not logged in. Allowing to proceed.')
        return
      }

      const sessionStep = sessionStorage.getItem('register_step')
      // If we have a valid step from Redux but URL doesn't match, update the URL
      if (currentStepCode && currentStepFromUrl !== currentStepCode) {
        const activeTestSession =
          takenTests.length > 0 && sessionStep === BACKEND_REGISTRATION_CODES.TUTOR_TEST

        const pathForStep = activeTestSession
          ? getPathForStep(sessionStep)
          : getPathForStep(currentStepCode)

        const isTestResult = search.includes('test-step')

        const kycOrBanner = [
          BACKEND_REGISTRATION_CODES.TUTOR_KYC,
          BACKEND_REGISTRATION_CODES.TUTOR_CREATE_BANNER,
          BACKEND_REGISTRATION_CODES.COMPLETED,
        ].includes(currentStepCode)

        const modifiedPath = kycOrBanner && isTestResult ? `pass-test${search}` : pathForStep
        console.log(
          '5555555555555555555555555555555555',
          role,
          'code url',
          currentStepCode,
          'gets path',
          pathForStep,
          'includeds step',
          isTestResult,
          'final path ',
          modifiedPath,
          '******************************'
        )
        navigate(`/${role}/register/${modifiedPath}`, { replace: true })
        return
      }

      // If we have a valid step from URL but Redux doesn't match, update Redux
      if (currentStepFromUrl && currentStepFromUrl !== currentStepCode) {
        console.log('Updating Redux to match URL:', currentStepFromUrl)
        dispatch(
          setStep({
            step: currentStepFromUrl,
            skipBackendUpdate: true,
          })
        )
        return
      }

      // Special case: After successful registration, we should be on email_verification
      if (currentStepCode === 'email_verification' && currentStepFromUrl !== 'email-verification') {
        console.log('Redirecting to email verification step')
        navigate(`/${role}/register/email-verification`, { replace: true })
        return
      }

      // If we get here, we're in a good state
      console.log('Registration step is in sync:', {
        urlStep: currentStepFromUrl,
        reduxStep: currentStepCode,
        path: window.location.pathname,
      })
    } catch (error) {
      console.error('Error in registration step synchronization:', error)
    } finally {
      setIsProcessingStepUpdate(false)
    }
  }, [
    currentStepFromUrl,
    currentStepCode,
    dispatch,
    accessToken,
    userData,
    role,
    navigate,
    pathname,
    // takenTests,
  ])

  // Handle invalid steps with a separate effect to avoid navigation during render
  useEffect(() => {
    const tabs = role === 'student' ? studentRegistrationSteps : tutorRegistrationSteps

    if (!tabs || !tabs.length) return

    // Check if the current step code is valid
    let currentStepObject = tabs.find((t) => t.code === effectiveStepCode)

    // If current step is not found, handle the redirection in this effect
    if (!currentStepObject) {
      console.warn(
        `Step code "${effectiveStepCode}" not found in tabs. Defaulting to create_account.`
      )
      currentStepObject = tabs.find((t) => t.code === 'create_account') || tabs[0]

      if (currentStepObject?.code) {
        const defaultPath = getPathForStep(currentStepObject.code)
        console.log('Redirecting to default step:', {
          code: currentStepObject.code,
          path: defaultPath,
        })
        navigate(`/${role}/register/${defaultPath}`, { replace: true })
      }
    }
  }, [effectiveStepCode, role, navigate])

  const getContent = () => {
    const tabs = role === 'student' ? studentRegistrationSteps : tutorRegistrationSteps

    if (!tabs || !tabs.length) {
      return {
        Component: null,
        tabs: [],
        activeStepIndex: -1,
      }
    }

    // Use effectiveStepCode which comes from Redux or URL
    let currentStepObject = tabs.find((t) => t.code === effectiveStepCode)
    let activeStepIndex = tabs.findIndex((t) => t.code === effectiveStepCode)

    // If step not found, fall back to a default without navigation (navigation is handled in the effect)
    if (!currentStepObject || activeStepIndex === -1) {
      console.warn(`Rendering fallback step because "${effectiveStepCode}" was not found.`)
      currentStepObject = tabs.find((t) => t.code === 'create_account') || tabs[0]
      activeStepIndex = tabs.findIndex((t) => t.code === currentStepObject?.code || '')
    }

    const ComponentToShow = currentStepObject?.component

    if (!ComponentToShow) {
      console.error('No component found for current step object:', currentStepObject)
      return {
        Component: <div>Error: No component available for this step</div>,
        tabs,
        activeStepIndex: -1,
      }
    }

    return {
      Component: <ComponentToShow role={role} />,
      tabs,
      activeStepIndex,
    }
  }

  // Use a ref to prevent infinite loops with step resets
  const lastAttemptedReset = useRef(null)

  useEffect(() => {
    if (isLoading) {
      return
    }
    const localAccessStorage = localStorage.getItem('access_token')
    // Only store the registration step if we have a valid user session

    // if ((accessToken || localAccessStorage) && userData) {
    if (accessToken && userData) {
      store.set('register_step', currentStepCode)
      console.log(`[REGISTRATION] Storing registration step: ${currentStepCode}`)
      // Reset our reset-prevention timestamp when user is logged in
      lastAttemptedReset.current = null
    } else if (currentStepCode !== 'create_account' && currentStepCode !== 'email_verification') {
      // If no user session but step isn't create_account or email_verification, reset it
      // We need to allow email_verification even without tokens during registration flow
      const now = Date.now()
      console.log('Check for user loading ============>')

      // Check if we've attempted this recently to prevent loops
      if (!lastAttemptedReset.current || now - lastAttemptedReset.current > 2000) {
        console.log(
          '[REGISTRATION] No valid session but step is not create_account or email_verification. Resetting.'
        )
        lastAttemptedReset.current = now

        // Clear any stored step in local storage
        store.remove('register_step')
        localStorage.removeItem('register_step')
        localStorage.removeItem('last_registration_step')

        // Dispatch once and exit - this prevents cascading resets
        dispatch(
          setStep({
            step: 'create_account',
            // step: 'profile_update',
            skipBackendUpdate: true,
          })
        )
      } else {
        console.log('[REGISTRATION] Preventing reset loop - ignoring reset attempt')
      }
    } else if (currentStepCode === 'email_verification') {
      // Special handling for email verification step - store it even without session
      console.log('[REGISTRATION] Allowing email_verification step without session')
      store.set('register_step', currentStepCode)
    }
  }, [currentStepCode, accessToken, userData, isLoading, dispatch])

  const { Component, tabs, activeStepIndex } = getContent()

  return (
    <>
      <div className="max-w-screen-xl w-full mx-auto font-Inter h-full text-[#F67443]">
        {!fullscreen && (
          <Progress step={activeStepIndex !== -1 ? activeStepIndex + 1 : 1} tabs={tabs} />
        )}
        {Component}
      </div>

      <MobileComplianceMobile
        open={isMobile}
        // onClose={() => setIsMobile(false)}
      />
    </>
  )
}

Register.defaultProps = {
  role: 'student',
}

Register.propTypes = {
  role: PropTypes.oneOf(['student', 'tutor']),
}
